import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaArrowLeft } from "react-icons/fa";
import Select from "react-select";

import { DataTableSkeleton, EmptyState, StatCard, StatusPill } from "components/admin";
import {
  getCompactSelectFieldStyles,
  SelectFieldOption,
} from "components/global/select-field-styles";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { workspaceApi } from "pages/app/workspace/services";
import {
  applyQuickActionToTotals,
  buildCurrentScoreboard,
  buildLiveState,
  buildScoresheetPayload,
  getAvailablePlayersByTeam,
  getScoreTemplate,
  normalizeTeamTotals,
} from "pages/app/workspace/sections/school-match-scoresheet.helpers";
import {
  ActionButtonRow,
  InlineField,
  InlineFormGrid,
  InlineInput,
  InlineLabel,
  InlineTextarea,
  PrimaryActionButton,
  QuickActionButton,
  QuickActionsGrid,
  ScoresheetBodyText,
  ScoresheetCard,
  ScoresheetCardHeader,
  ScoresheetColumn,
  ScoresheetEyebrow,
  ScoresheetGrid,
  ScoresheetStack,
  ScoresheetTitle,
  SecondaryActionButton,
  SegmentedRow,
  SegmentButton,
  TeamFieldGrid,
  TeamScoreCard,
  TeamScoreGrid,
  TeamScoreHeader,
  TeamScoreName,
  TeamScoreSubtext,
  TimelineHeader,
  TimelineItem,
  TimelineMeta,
  TimelineNote,
  TimelineStack,
  TimelineTitle,
} from "pages/app/workspace/sections/styles/school-match-scoresheet.inline.styles";
import {
  SchoolRosterProfilePrimaryButton,
  SchoolRosterProfileSecondaryButton,
} from "pages/app/workspace/styles/school-roster-profile.page.inline.styles";
import { ApiErrorLike } from "types/api";
import { formatDateTime, getApiErrorMessage } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";

const STATUS_OPTIONS = ["draft", "live", "paused", "finalized"];

const isNotFoundError = (error: unknown) => (error as ApiErrorLike)?.response?.status === 404;

const normalizeMinuteMark = (value: string) => {
  if (!String(value || "").trim()) {
    return undefined;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const SchoolMatchScoresheetPage: React.FC = () => {
  const { matchId } = useParams<{
    matchId: string;
  }>();
  const history = useHistory();
  const location = useLocation<{
    backTo?: string;
  }>();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();

  const matchQuery = useQuery({
    queryKey: ["workspace-school-match-detail", matchId],
    queryFn: () => workspaceApi.schoolMatches.get(matchId),
    enabled: Boolean(matchId),
  });

  const scoresheetQuery = useQuery({
    queryKey: ["workspace-school-match-scoresheet", matchId],
    queryFn: async () => {
      try {
        return await workspaceApi.schoolMatchScoresheets.get(matchId);
      } catch (error) {
        if (isNotFoundError(error)) {
          return null;
        }

        throw error;
      }
    },
    enabled: Boolean(matchId),
  });

  const liveEventsQuery = useQuery({
    queryKey: ["workspace-school-match-live-events", matchId],
    queryFn: () => workspaceApi.schoolMatchLiveEvents.list(matchId),
    enabled: Boolean(matchId),
  });

  const rostersQuery = useQuery({
    queryKey: ["workspace-school-match-rosters", matchId],
    queryFn: () => workspaceApi.schoolRosters.list(matchId),
    enabled: Boolean(matchId),
  });

  const teamPlayersQuery = useQuery({
    queryKey: [
      "workspace-school-match-team-players",
      matchId,
      matchQuery.data?.home_school_team?.id,
      matchQuery.data?.away_school_team?.id,
    ],
    queryFn: async () => {
      const teams = [matchQuery.data?.home_school_team, matchQuery.data?.away_school_team].filter(Boolean);
      const responses = await Promise.all(
        teams.map((team: any) => workspaceApi.schoolTeamPlayers.list(team.id)),
      );

      return teams.reduce<Record<string, any[]>>((accumulator, team: any, index: number) => {
        accumulator[String(team.id)] = responses[index] || [];
        return accumulator;
      }, {});
    },
    enabled:
      Boolean(matchId) &&
      Boolean(matchQuery.data?.home_school_team?.id) &&
      Boolean(matchQuery.data?.away_school_team?.id),
  });

  const detailedMatch = matchQuery.data;
  const scoreTemplate = useMemo(
    () => getScoreTemplate(scoresheetQuery.data || detailedMatch),
    [detailedMatch, scoresheetQuery.data],
  );
  const availablePlayersByTeam = useMemo(
    () =>
      getAvailablePlayersByTeam({
        rosters: rostersQuery.data || [],
        teamPlayersByTeam: teamPlayersQuery.data || {},
      }),
    [rostersQuery.data, teamPlayersQuery.data],
  );

  const [status, setStatus] = useState("draft");
  const [periodLabel, setPeriodLabel] = useState("");
  const [minuteMark, setMinuteMark] = useState("");
  const [scoresheetNotes, setScoresheetNotes] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [eventNote, setEventNote] = useState("");
  const [homeTotals, setHomeTotals] = useState<Record<string, any>>({});
  const [awayTotals, setAwayTotals] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!detailedMatch) {
      return;
    }

    const existingScoresheet = scoresheetQuery.data || detailedMatch?.scoresheet_summary || null;
    const currentScoreboard = buildCurrentScoreboard(
      scoreTemplate,
      existingScoresheet?.home_totals,
      existingScoresheet?.away_totals,
    );
    const liveState = buildLiveState(scoreTemplate, existingScoresheet?.live_state);

    setHomeTotals(currentScoreboard.home);
    setAwayTotals(currentScoreboard.away);
    setStatus(existingScoresheet?.status || "draft");
    setPeriodLabel(
      liveState.current_period ||
        scoreTemplate.periods?.[0]?.label ||
        scoreTemplate.live_state_defaults?.current_period ||
        "",
    );
    setMinuteMark(
      liveState.minute_mark === null || liveState.minute_mark === undefined
        ? ""
        : String(liveState.minute_mark),
    );
    setScoresheetNotes(existingScoresheet?.notes || "");
    setSelectedTeamId(String(detailedMatch.home_school_team?.id || ""));
    setSelectedPlayerId("");
    setEventNote("");
  }, [detailedMatch, scoreTemplate, scoresheetQuery.data]);

  const canManageScoresheet = hasPortalAction(portalAccess, "school_matches.manage");
  const backTo = location.state?.backTo || "/workspace/schedule?view=list&focus=scoring";
  const manageScoresheetsPath = "/workspace/schedule?view=list&focus=scoring";

  const invalidateRealtimeQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["workspace-school-matches"] }),
      queryClient.invalidateQueries({ queryKey: ["workspace-school-match-detail", matchId] }),
      queryClient.invalidateQueries({ queryKey: ["workspace-school-match-scoresheet", matchId] }),
      queryClient.invalidateQueries({ queryKey: ["workspace-school-match-live-events", matchId] }),
    ]);
  };

  const saveScoresheetMutation = useMutation({
    mutationFn: async ({
      nextStatus,
      successMessage,
    }: {
      nextStatus?: string;
      successMessage: string;
    }) => {
      const payload = buildScoresheetPayload({
        homeTotals,
        awayTotals,
        liveState: {
          current_period: periodLabel,
          minute_mark: normalizeMinuteMark(minuteMark),
        },
        notes: scoresheetNotes,
        scoringFormat: scoreTemplate.scoring_format,
        status: nextStatus || status,
        template: scoreTemplate,
      });

      return {
        response: await workspaceApi.schoolMatchScoresheets.save(matchId, payload),
        successMessage,
      };
    },
    onSuccess: async ({ response, successMessage }) => {
      setStatus(response?.status || status);
      toast.success(successMessage);
      await invalidateRealtimeQueries();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to save this scoresheet."));
    },
  });

  const createLiveEventMutation = useMutation({
    mutationFn: async (action: any) => {
      const homeTeamId = String(detailedMatch?.home_school_team?.id || "");
      const awayTeamId = String(detailedMatch?.away_school_team?.id || "");
      const nextHomeTotals = normalizeTeamTotals(scoreTemplate, homeTotals);
      const nextAwayTotals = normalizeTeamTotals(scoreTemplate, awayTotals);

      if (String(selectedTeamId) === homeTeamId) {
        Object.assign(
          nextHomeTotals,
          applyQuickActionToTotals(scoreTemplate, nextHomeTotals, action),
        );
      } else if (String(selectedTeamId) === awayTeamId) {
        Object.assign(
          nextAwayTotals,
          applyQuickActionToTotals(scoreTemplate, nextAwayTotals, action),
        );
      }

      const nextStatus = status === "draft" ? "live" : status;

      return workspaceApi.schoolMatchLiveEvents.create(matchId, {
        school_team_id: selectedTeamId || undefined,
        player_id: selectedPlayerId || undefined,
        event_type: action.event_type,
        period_label: periodLabel || undefined,
        minute_mark: normalizeMinuteMark(minuteMark),
        note: eventNote || undefined,
        scoreboard: {
          home: nextHomeTotals,
          away: nextAwayTotals,
        },
        state_snapshot: {
          ...buildLiveState(scoreTemplate, {
            current_period: periodLabel,
            minute_mark: normalizeMinuteMark(minuteMark),
            status: nextStatus,
          }),
          status: nextStatus,
        },
        payload: {
          action_key: action.key,
          action_label: action.label,
          template_key: scoreTemplate.sport_key,
        },
        metadata: {
          template_key: scoreTemplate.sport_key,
        },
      });
    },
    onSuccess: async () => {
      setEventNote("");
      setSelectedPlayerId("");
      toast.success("Live event recorded.");
      await invalidateRealtimeQueries();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to record this live event."));
    },
  });

  const compactSelectStyles = useMemo(() => getCompactSelectFieldStyles(), []);
  const periodSelectOptions = useMemo<SelectFieldOption[]>(
    () =>
      (scoreTemplate.periods || []).map((period: any) => ({
        label: period.label,
        value: String(period.label),
      })),
    [scoreTemplate],
  );
  const teamSelectOptions = useMemo<SelectFieldOption[]>(
    () =>
      [
        {
          label: detailedMatch?.home_school_team?.name || "Home team",
          value: String(detailedMatch?.home_school_team?.id || ""),
        },
        {
          label: detailedMatch?.away_school_team?.name || "Away team",
          value: String(detailedMatch?.away_school_team?.id || ""),
        },
      ].filter((option) => Boolean(option.value)),
    [detailedMatch],
  );
  const playerSelectOptions = useMemo<SelectFieldOption[]>(() => {
    const availablePlayers = availablePlayersByTeam[String(selectedTeamId)] || [];

    return [
      { label: "Team / match event", value: "" },
      ...availablePlayers.map((player: any) => ({
        label: player.full_name || player.player_name || "Unknown player",
        value: String(player.id),
      })),
    ];
  }, [availablePlayersByTeam, selectedTeamId]);
  const timelineEvents = (liveEventsQuery.data || []).slice().sort((left: any, right: any) => {
    return Number(right.sequence_number || 0) - Number(left.sequence_number || 0);
  });
  const isLoading =
    matchQuery.isLoading ||
    scoresheetQuery.isLoading ||
    liveEventsQuery.isLoading ||
    rostersQuery.isLoading ||
    teamPlayersQuery.isLoading;

  const pageTitle = detailedMatch
    ? `${detailedMatch.home_school_team?.name || "Home"} vs ${detailedMatch.away_school_team?.name || "Away"}`
    : "Scoresheet";

  const headerActions = useMemo(
    () => (
      <>
        <SchoolRosterProfileSecondaryButton onClick={() => history.push(backTo)} type="button">
          <FaArrowLeft size={12} />
          Back
        </SchoolRosterProfileSecondaryButton>
        {canManageScoresheet ? (
          <SchoolRosterProfilePrimaryButton
            onClick={() => history.push(manageScoresheetsPath)}
            type="button"
          >
            Manage scoresheets
          </SchoolRosterProfilePrimaryButton>
        ) : null}
      </>
    ),
    [backTo, canManageScoresheet, history, manageScoresheetsPath],
  );

  return (
    <AppLayout route="/workspace/schedule">
      <SchoolOpsHeader actions={headerActions} title={pageTitle} />

      {isLoading ? (
        <DataTableSkeleton columns={2} rows={8} />
      ) : !detailedMatch ? (
        <EmptyState
          description="This match could not be loaded for scoring."
          title="Scoresheet not found"
        />
      ) : (
        <>
          <StatsGridRow
            homeLabel={detailedMatch.home_school_team?.name || "Home team"}
            homeMeta={detailedMatch.home_school_team?.school_name || "School pending"}
            homeValue={homeTotals.display_score || "0"}
            awayLabel={detailedMatch.away_school_team?.name || "Away team"}
            awayMeta={detailedMatch.away_school_team?.school_name || "School pending"}
            awayValue={awayTotals.display_score || "0"}
            status={status}
          />

          <ScoresheetStack>
            <ScoresheetGrid>
              <ScoresheetColumn>
                <ScoresheetCard>
                  <ScoresheetCardHeader>
                    <div>
                      <ScoresheetEyebrow>
                        {scoreTemplate.sport_name || detailedMatch.sport_name}
                      </ScoresheetEyebrow>
                      <ScoresheetTitle>Match state</ScoresheetTitle>
                      <ScoresheetBodyText>
                        Move the fixture between draft, live, paused, and finalized while keeping
                        the current period and minute in sync.
                      </ScoresheetBodyText>
                    </div>
                  </ScoresheetCardHeader>

                  <SegmentedRow>
                    {STATUS_OPTIONS.map((value) => (
                      <SegmentButton
                        $active={status === value}
                        key={value}
                        onClick={() => setStatus(value)}
                      >
                        {value}
                      </SegmentButton>
                    ))}
                  </SegmentedRow>

                  <InlineFormGrid>
                    <InlineField>
                      <InlineLabel>Current period</InlineLabel>
                      <Select
                        aria-label="Current period"
                        classNamePrefix="scoresheet-period-select"
                        inputId="scoresheet-period"
                        isSearchable={false}
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        onChange={(option) => setPeriodLabel(option?.value ?? "")}
                        options={periodSelectOptions}
                        placeholder="Select period"
                        styles={compactSelectStyles}
                        value={
                          periodSelectOptions.find((option) => option.value === periodLabel) || null
                        }
                      />
                    </InlineField>
                    <InlineField>
                      <InlineLabel>Minute / mark</InlineLabel>
                      <InlineInput
                        min={0}
                        onChange={(event) => setMinuteMark(event.target.value)}
                        placeholder="12"
                        type="number"
                        value={minuteMark}
                      />
                    </InlineField>
                  </InlineFormGrid>

                  <InlineField>
                    <InlineLabel>Sheet notes</InlineLabel>
                    <InlineTextarea
                      onChange={(event) => setScoresheetNotes(event.target.value)}
                      placeholder="Weather, officiating notes, injury delay, or scorer context."
                      rows={4}
                      value={scoresheetNotes}
                    />
                  </InlineField>

                  <ActionButtonRow>
                    <PrimaryActionButton
                      disabled={!canManageScoresheet || saveScoresheetMutation.isPending}
                      onClick={() =>
                        saveScoresheetMutation.mutate({
                          successMessage: "Scoresheet saved.",
                        })
                      }
                    >
                      {saveScoresheetMutation.isPending ? "Saving..." : "Save snapshot"}
                    </PrimaryActionButton>
                    <SecondaryActionButton
                      disabled={!canManageScoresheet || saveScoresheetMutation.isPending}
                      onClick={() =>
                        saveScoresheetMutation.mutate({
                          nextStatus: "live",
                          successMessage: "Match marked live.",
                        })
                      }
                    >
                      Set live
                    </SecondaryActionButton>
                    <SecondaryActionButton
                      disabled={!canManageScoresheet || saveScoresheetMutation.isPending}
                      onClick={() =>
                        saveScoresheetMutation.mutate({
                          nextStatus: "paused",
                          successMessage: "Match paused.",
                        })
                      }
                    >
                      Pause
                    </SecondaryActionButton>
                    <SecondaryActionButton
                      disabled={!canManageScoresheet || saveScoresheetMutation.isPending}
                      onClick={() =>
                        saveScoresheetMutation.mutate({
                          nextStatus: "finalized",
                          successMessage: "Match finalized.",
                        })
                      }
                    >
                      Finalize
                    </SecondaryActionButton>
                  </ActionButtonRow>
                </ScoresheetCard>

                <ScoresheetCard>
                  <ScoresheetCardHeader>
                    <div>
                      <ScoresheetEyebrow>Sport Specific</ScoresheetEyebrow>
                      <ScoresheetTitle>Quick actions</ScoresheetTitle>
                      <ScoresheetBodyText>
                        These buttons follow the scoring language for{" "}
                        {scoreTemplate.sport_name || "this sport"} so users can log the moments
                        that matter without typing everything manually.
                      </ScoresheetBodyText>
                    </div>
                  </ScoresheetCardHeader>

                  <InlineFormGrid>
                    <InlineField>
                      <InlineLabel>Team</InlineLabel>
                      <Select
                        aria-label="Team"
                        classNamePrefix="scoresheet-team-select"
                        inputId="scoresheet-team"
                        isSearchable={false}
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        onChange={(option) => {
                          setSelectedTeamId(option?.value ?? "");
                          setSelectedPlayerId("");
                        }}
                        options={teamSelectOptions}
                        placeholder="Select team"
                        styles={compactSelectStyles}
                        value={
                          teamSelectOptions.find(
                            (option) => option.value === String(selectedTeamId),
                          ) || null
                        }
                      />
                    </InlineField>
                    <InlineField>
                      <InlineLabel>Player</InlineLabel>
                      <Select
                        aria-label="Player"
                        classNamePrefix="scoresheet-player-select"
                        inputId="scoresheet-player"
                        isSearchable
                        menuPlacement="auto"
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        onChange={(option) => setSelectedPlayerId(option?.value ?? "")}
                        options={playerSelectOptions}
                        placeholder="Team / match event"
                        styles={compactSelectStyles}
                        value={
                          playerSelectOptions.find(
                            (option) => option.value === String(selectedPlayerId),
                          ) || null
                        }
                      />
                    </InlineField>
                  </InlineFormGrid>

                  <InlineField>
                    <InlineLabel>Event note</InlineLabel>
                    <InlineTextarea
                      onChange={(event) => setEventNote(event.target.value)}
                      placeholder="Optional scorer note, assist, dismissal detail, or referee context."
                      rows={3}
                      value={eventNote}
                    />
                  </InlineField>

                  <QuickActionsGrid>
                    {(scoreTemplate.quick_actions || []).map((action: any) => (
                      <QuickActionButton
                        disabled={!canManageScoresheet || createLiveEventMutation.isPending}
                        key={action.key}
                        onClick={() => createLiveEventMutation.mutate(action)}
                      >
                        {createLiveEventMutation.isPending ? "Recording..." : action.label}
                      </QuickActionButton>
                    ))}
                  </QuickActionsGrid>
                </ScoresheetCard>

                <ScoresheetCard>
                  <ScoresheetCardHeader>
                    <div>
                      <ScoresheetEyebrow>Manual Corrections</ScoresheetEyebrow>
                      <ScoresheetTitle>Adjust the board</ScoresheetTitle>
                      <ScoresheetBodyText>
                        Use the field editor when the scorer needs to correct totals, overs, sets,
                        or team points directly.
                      </ScoresheetBodyText>
                    </div>
                  </ScoresheetCardHeader>

                  <TeamScoreGrid>
                    {[
                      {
                        key: "home",
                        team: detailedMatch.home_school_team,
                        totals: homeTotals,
                        setTotals: setHomeTotals,
                      },
                      {
                        key: "away",
                        team: detailedMatch.away_school_team,
                        totals: awayTotals,
                        setTotals: setAwayTotals,
                      },
                    ].map((entry: any) => (
                      <TeamScoreCard key={entry.key}>
                        <TeamScoreHeader>
                          <div>
                            <TeamScoreName>{entry.team?.name || "Team"}</TeamScoreName>
                            <TeamScoreSubtext>
                              {entry.team?.school_name || "School pending"}
                            </TeamScoreSubtext>
                          </div>
                          <StatusPill value={entry.totals.display_score || "0"} />
                        </TeamScoreHeader>

                        <TeamFieldGrid>
                          {(scoreTemplate.scoreboard_fields || []).map((field: any) => (
                            <InlineField key={`${entry.key}-${field.key}`}>
                              <InlineLabel>{field.label}</InlineLabel>
                              <InlineInput
                                min={field.min}
                                onChange={(event) =>
                                  entry.setTotals((current: Record<string, any>) => ({
                                    ...current,
                                    [field.key]: event.target.value,
                                  }))
                                }
                                step={field.step}
                                type={field.kind === "number" ? "number" : "text"}
                                value={entry.totals[field.key] ?? ""}
                              />
                            </InlineField>
                          ))}
                        </TeamFieldGrid>
                      </TeamScoreCard>
                    ))}
                  </TeamScoreGrid>

                  <ActionButtonRow>
                    <PrimaryActionButton
                      disabled={!canManageScoresheet || saveScoresheetMutation.isPending}
                      onClick={() =>
                        saveScoresheetMutation.mutate({
                          successMessage: "Scoreboard adjustments saved.",
                        })
                      }
                    >
                      Save board changes
                    </PrimaryActionButton>
                  </ActionButtonRow>
                </ScoresheetCard>
              </ScoresheetColumn>

              <ScoresheetColumn>
                <ScoresheetCard>
                  <ScoresheetCardHeader>
                    <div>
                      <ScoresheetEyebrow>Timeline</ScoresheetEyebrow>
                      <ScoresheetTitle>Recorded match events</ScoresheetTitle>
                      <ScoresheetBodyText>
                        Every logged action appears here in reverse sequence so the scorer can
                        review the live narrative as it unfolds.
                      </ScoresheetBodyText>
                    </div>
                  </ScoresheetCardHeader>

                  {!timelineEvents.length ? (
                    <EmptyState
                      description="Quick actions and manual live events will appear here once the scorer starts recording the match."
                      title="No live events yet"
                    />
                  ) : (
                    <TimelineStack>
                      {timelineEvents.map((event: any) => (
                        <TimelineItem key={event.id}>
                          <TimelineHeader>
                            <TimelineTitle>
                              {event.payload?.action_label || event.event_type}
                              {event.school_team_name ? ` - ${event.school_team_name}` : ""}
                            </TimelineTitle>
                            <TimelineMeta>
                              #{event.sequence_number} - {event.period_label || "Match flow"} -{" "}
                              {event.minute_mark ?? "?"}
                              {event.minute_mark !== null && event.minute_mark !== undefined
                                ? "'"
                                : ""}
                            </TimelineMeta>
                          </TimelineHeader>
                          <TimelineMeta>
                            {event.player_name ? `${event.player_name} - ` : ""}
                            {event.recorded_by_name ? `${event.recorded_by_name} - ` : ""}
                            {formatDateTime(event.occurred_at)}
                          </TimelineMeta>
                          {event.note ? <TimelineNote>{event.note}</TimelineNote> : null}
                        </TimelineItem>
                      ))}
                    </TimelineStack>
                  )}
                </ScoresheetCard>
              </ScoresheetColumn>
            </ScoresheetGrid>
          </ScoresheetStack>
        </>
      )}
    </AppLayout>
  );
};

const StatsGridRow: React.FC<{
  awayLabel: string;
  awayMeta: string;
  awayValue: string;
  homeLabel: string;
  homeMeta: string;
  homeValue: string;
  status: string;
}> = ({ awayLabel, awayMeta, awayValue, homeLabel, homeMeta, homeValue, status }) => (
  <div className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
    <StatCard helper={homeMeta} label={homeLabel} value={homeValue} />
    <StatCard helper={awayMeta} label={awayLabel} value={awayValue} />
    <StatCard label="Sheet status" value={<StatusPill value={status} />} />
    <StatCard label="Format" value="Live scoresheet" />
  </div>
);

export default SchoolMatchScoresheetPage;
